import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Button, Dropdown, Form, InputGroup, Row, Col } from 'react-bootstrap';
import { BiFilterAlt } from 'react-icons/bi';
import DiscoveryTooltip from 'DiscoveryTooltip';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { getProviders } from 'Secure/api';
import { listDataGraphs } from 'DataGraph/dataGraphApi';
import {
  reportSources, reportNoun, reportCounts, FindingsList, NeededPanel, ReportWarnings, objectSummaryParts,
} from 'DataGraph/ObjectReport';
import {
  getSubset, createSubset, updateSubset, deleteSubset, listStaging,
  startPreview, getPreview, startSubsetRun, listSubsetRuns, cancelRun, deleteRun, listMaskingEngineJobs, errorText,
  buildInventoryFromProfile, buildInventoryFromSourceDiscovery,
} from '../tdmApi';
import {
  fmtNum, fmtBytes, fmtWhen, parseSeeds, uniqueSeeds, SEED_FILE_MAX_BYTES, MASKING_MODE_LABEL, RUN_STATUS_TONE, kindForEngine, unitNoun, enginesForMode,
  isDelphixMode, isDelphixEngine, isMaskableStaging, maskableStagingTypes, isTdmConnection, slugPreview, parentOf,
  bytesToSize, sizeToBytes, budgetBasis, SIZE_UNITS, SALESFORCE_SANDBOXES, sandboxForBytes,
} from '../tdmUtils';
import RunStepStrip from '../RunStepStrip';
import SectionTitle, { HelpIcon } from '../SectionTitle';
import usePolling from '../usePolling';
import SourcesSection, { chosenGraph, sourceGraphProblem } from './SourcesSection';
import { DiscoverWarnings } from './RunDetail';

import SourcePickerView from './SourcePickerView';
import { useConfirm } from 'Utils/ConfirmDialog';
import Breadcrumbs from 'Utils/Breadcrumbs';

// The sources of a subset imported from staging, as its subset.json described them: names and
// engines from the platform that exported it, not connections on this one.
const ImportedSourcesSection = ({ sources, sharedKeyName }) => (
  <div className='tdm-section' data-testid='tdm-imported-sources'>
    <SectionTitle title='Sources · imported' help='As described by the platform that exported this subset. They are names, not data sources on this platform.' />
    {(sources || []).length === 0 && <div className='tdm-empty'>The subset file lists no sources.</div>}
    {(sources || []).map((m) => (
      <div className='tdm-note' key={m.slug || m.dataSourceName}>
        {m.engine ? `${String(m.engine).toUpperCase()} · ` : ''}<strong>{m.dataSourceName || m.slug}</strong>{m.parent ? ' (parent)' : ''}
        <span className='tdm-muted'>{` · ${m.slug}`}{m.keyTable ? ` · ${m.keyTable}${m.keyColumn ? `.${m.keyColumn}` : ''}` : ''}</span>
      </div>
    ))}
    {sharedKeyName && <div className='tdm-note'>Shared key: {sharedKeyName}</div>}
  </div>
);

const POLL_MS = 5000;

// How often a Manual build asks again while a source is being profiled first (see buildFromProfile).
// Read per call so a test can shorten it.
const profilePollMs = () => Number(process.env.REACT_APP_TDM_PROFILE_POLL_MS) || 5000;

const MASKING_MODES = [
  ['PLATFORM', 'Platform masking (default)', 'The C2 masking service masks every row as it is extracted, with the subset\'s inventory, so unmasked data is never staged. Staged files are encrypted.'],
  ['DELPHIX_MANAGED', 'Delphix', 'The Delphix engine masks the staged files in place through the Delphix integration, with the subset\'s inventory. Staged files are not encrypted: the engine reads them.'],
  ['DELPHIX_EXISTING_JOB', 'Delphix (existing job)', 'Run a file masking job that already exists on the Delphix engine against this run\'s staged files, in place. No discovery; the job\'s own ruleset decides what is masked. Staged files are not encrypted.'],
  ['NONE', 'No masking', 'Rows are staged as read (encrypted) and deployed unmasked. Use only for non-sensitive data.'],
];

// Where the inventory comes from, for the modes that mask from one. The stored values (DISCOVER /
// PROFILE / SOURCE_DISCOVERY) are the platform's; the titles are what the user sees.
const INVENTORY_MODES = [
  ['DISCOVER', 'Discover, then mask', 'Every run discovers each source database and builds the inventory from what it finds, before anything is staged. Columns you decided on in the inventory editor keep your choice.'],
  ['PROFILE', 'Manual', 'Build the inventory from the sources\' fingerprint — every column, nothing marked — and choose what to mask yourself. A source with no fingerprint is fingerprinted first. Runs never discover.'],
  ['SOURCE_DISCOVERY', 'Last Discover', 'Build the inventory from the last discovery of each source: columns it found sensitive data in are pre-marked. Edit it afterwards; runs never discover.'],
];

const blankForm = () => ({
  name: '', description: '', mode: 'ALL', where: '', seedsText: '',
  budgetSize: '', budgetSizeUnit: 'MB', budgetCustom: false, legacyBudgetMaxRows: null,
  maskingMode: 'PLATFORM', maskingRegisteredServiceId: '', externalMaskingJobId: '', stagingId: '', inventoryMode: 'DISCOVER',
});

const fromResponse = (s) => ({
  name: s.name || '', description: s.description || '',
  mode: (s.selection && s.selection.mode) || 'ALL',
  where: (s.selection && s.selection.where) || '', seedsText: ((s.selection && s.selection.seeds) || []).join('\n'),
  budgetSize: bytesToSize(s.budgetMaxBytes).value, budgetSizeUnit: bytesToSize(s.budgetMaxBytes).unit,
  // UI only, for a Salesforce parent: a size that is no sandbox preset reopens as Custom.
  budgetCustom: sandboxForBytes(s.budgetMaxBytes) == null,
  // A row budget from before the budget became a size (2026-10-21): shown, and replaced on save.
  legacyBudgetMaxRows: s.budgetMaxBytes == null && s.budgetMaxRows != null ? s.budgetMaxRows : null,
  maskingMode: s.maskingMode || 'PLATFORM', maskingRegisteredServiceId: s.maskingRegisteredServiceId || '',
  externalMaskingJobId: s.externalMaskingJobId || '',
  stagingId: String(s.stagingId || ''), inventoryMode: s.inventoryMode || 'DISCOVER',
});

// A source row as the editor holds it, from a connection (new) or from the saved subset (existing).
// `dataGraphId` is the data graph it is pulled through; a source with exactly one gets it at once.
const engineLabel = (p) => (p ? `${p.vendorDisplayName || p.name}${p.vendorDisplayName && p.name && p.vendorDisplayName !== p.name ? ` · ${p.name}` : ''}` : '');
const sourceFromConnection = (c, graphs) => ({
  dataSourceId: c.id, dataSourceName: c.name, engine: c.type || c.engine, parent: false, slug: slugPreview(c.name),
  dataGraphId: graphs && graphs.length === 1 ? graphs[0].id : null,
});
const sourcesFromResponse = (s) => ((s && s.sources) || []).map((m) => ({ ...m }));

// The kind follows the parent's engine; the platform derives the same and refuses a mismatch.
// The sources ride on the subset itself — one save call carries the selection AND where it reads from.
// The key, the shared key name, the hop limit and the object list are not sent: they belong to each
// source's data graph. So are the start table and the seed column: the parent graph's key table and key
// column (the platform fills them in). Seed ids go once each.
const toBody = (f, sources, parentEngine) => ({
  name: f.name, description: f.description || null,
  sources: sources.map((m) => ({ dataSourceId: m.dataSourceId, parent: Boolean(m.parent), slug: m.slug || null, dataGraphId: m.dataGraphId != null ? Number(m.dataGraphId) : null })),
  kind: kindForEngine(parentEngine),
  selection: {
    mode: f.mode,
    ...(f.mode === 'WHERE' ? { where: f.where } : {}),
    ...(f.mode === 'SEEDS' ? { seeds: uniqueSeeds(parseSeeds(f.seedsText)).ids } : {}),
  },
  // The budget is always a hard stop (2026-10-21). What a source pulls — everything connected or a list
  // with its rules, the hop limit — is its data graph's (contract 3.0: the subset has no traversal).
  // The budget is a size: the worker prices each row at its source size (Salesforce: its storage
  // accounting), the unit the preview's bytes are in. A legacy row budget is dropped on save.
  budgetMaxRows: null, budgetMaxBytes: sizeToBytes(f.budgetSize, f.budgetSizeUnit), budgetUnit: 'BYTES', hardStop: true,
  maskingMode: f.maskingMode,
  // PLATFORM: an explicit C2 engine or the registry default; the Delphix modes always name their engine.
  maskingRegisteredServiceId: f.maskingMode !== 'NONE' && f.maskingRegisteredServiceId ? f.maskingRegisteredServiceId : null,
  externalMaskingJobId: f.maskingMode === 'DELPHIX_EXISTING_JOB' && f.externalMaskingJobId ? String(f.externalMaskingJobId) : null,
  stagingId: Number(f.stagingId),
  // Never set from here: in DISCOVER mode the first run discovers and the server clears the flag
  // once it has, so there is nothing for the user to tick. null = leave the stored flag alone.
  discoverOnNextRun: null,
  inventoryMode: f.inventoryMode || 'DISCOVER',
});

// What a from-source-discovery build used: per source, the discovery run (or none) and the columns now
// marked; then everything the build left for a manual decision.
const SourceDiscoverySummary = ({ result }) => {
  const sources = (result && result.sources) || [];
  const warnings = (result && result.warnings) || [];
  return (
    <div className='tdm-note mt-2' data-testid='tdm-source-discovery-summary'>
      <ul className='mb-1 pl-3'>
        {sources.map((s) => (
          <li key={`${s.dataSourceId}-${s.slug}`} data-testid={`tdm-source-discovery-${s.slug}`}>
            <strong>{s.slug}</strong>:{' '}
            {s.discoveryJobId
              ? <>discovery <Link to={`/results/${s.discoveryJobId}`}>#{s.discoveryJobId}</Link>{s.discoveryFinishedAt ? ` (${fmtWhen(s.discoveryFinishedAt)})` : ''} · {fmtNum(s.columnsMarked || 0)} column{s.columnsMarked === 1 ? '' : 's'} marked</>
              : <span className='text-warning'>no discovery used — listed unmarked</span>}
          </li>
        ))}
      </ul>
      {warnings.length > 0 && (
        <>
          <div className='tdm-muted'>Needs your decision ({warnings.length}):</div>
          <ul className='mb-0 pl-3' data-testid='tdm-source-discovery-warnings'>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </>
      )}
    </div>
  );
};

// The isolation report's lists stay short here: past this many rows they fold behind "Show all".
const REPORT_LIMIT = 10;
const engineOfReport = (r, sources) => ((sources || []).find((m) => String(m.dataSourceId) === String(r.dataSourceId)) || {}).engine;
const reportLabel = (r) => r.dataSourceName || r.slug || `data source #${r.dataSourceId}`;

// Before: per source, what the plan already says (contract 3.0) — its findings (an isolated object is an
// error; it does not stop the run) and what the list needs from objects off it. Built from the plans when
// the preview starts, so it shows while the counts run. The preview's estimates list the tables. Never blocks.
const PreviewObjectReport = ({ report, sources }) => {
  const rows = reportSources(report).filter((r) => {
    const c = reportCounts(r);
    return c.errors + c.warnings + c.infos > 0 || c.needed > 0 || (r.warnings || []).length > 0;
  });
  if (rows.length === 0) return null;
  return (
    <div data-testid='tdm-preview-report'>
      {rows.map((r) => {
        const noun = reportNoun(engineOfReport(r, sources));
        return (
          <div className='mt-2' key={r.slug || r.dataSourceId} data-testid={`tdm-preview-report-${r.slug}`}>
            <div className='tdm-note mb-1'><strong>{reportLabel(r)}</strong>{r.dataGraphName ? ` · data graph ${r.dataGraphName}` : ''}</div>
            <FindingsList findings={r.findings} limit={REPORT_LIMIT} testId={`tdm-preview-findings-${r.slug}`} />
            <ReportWarnings warnings={r.warnings} testId={`tdm-preview-report-warnings-${r.slug}`} />
            {reportCounts(r).needed > 0 && (
              <NeededPanel needed={r.needed} counted={Boolean(r.counted)} noun={noun} testId={`tdm-preview-needed-${r.slug}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// After: a run row carries only its summary's counts (`objectSummary`, 2026-11-02) as one line —
// "1 failed · 2 warnings · 3 needed" — and nothing
// when there is nothing to say. The lists are on the run's detail page (the row's Details link). The
// run's status is untouched.
const RunObjectSummary = ({ run }) => {
  const parts = objectSummaryParts(run.objectSummary);
  if (parts.length === 0) return null;
  return (
    <div className='tdm-note tdm-run-summary' data-testid={`tdm-run-${run.id}-summary`}>
      {parts.map((p, i) => (
        <React.Fragment key={p.key}>
          {i > 0 && ' · '}
          <span className={p.danger ? 'tdm-run-summary__failed' : undefined} data-testid={`tdm-run-${run.id}-summary-${p.key}`}>{p.text}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

const PreviewPanel = ({ preview, budgetMaxBytes, onPreview, canPreview, sources }) => {
  const objectReport = preview && preview.objectReport ? <PreviewObjectReport report={preview.objectReport} sources={sources} /> : null;
  if (!preview || preview.state === 'NONE') {
    return (
      <div className='tdm-empty'>
        No preview yet. Preview runs a dry traversal on the worker and reports counts per table.
        {onPreview && (
          <div className='mt-2'>
            <Button size='sm' variant='outline-primary' onClick={onPreview} disabled={!canPreview} data-testid='tdm-preview-start'>Preview counts</Button>
          </div>
        )}
      </div>
    );
  }
  if (preview.state === 'RUNNING') return <div><div className='tdm-note'>Preview running… started {fmtWhen(preview.startedAt)}</div>{objectReport}</div>;
  if (preview.state === 'FAILED') return <div><div className='tdm-error'>Preview failed: {preview.message || 'no details'}</div>{objectReport}</div>;
  const r = preview.result || {};
  const per = Array.isArray(r.perTable) ? r.perTable : [];
  const total = r.total || {};
  const over = budgetMaxBytes && total.bytes > Number(budgetMaxBytes);
  return (
    <div data-testid='tdm-preview'>
      <div className='tdm-note'>dry traversal · {fmtWhen(preview.finishedAt)}</div>
      <div className='tdm-table tdm-table--preview mt-2'>
        <div className='tdm-table__head'><span>Table</span><span className='tdm-num'>Rows</span><span className='tdm-num'>Bytes</span><span>Method</span></div>
        {per.slice(0, 40).map((t, i) => (
          <div className='tdm-table__row' key={`${t.source}-${t.name}-${i}`}>
            <span className='tdm-mono'>{t.source ? `${t.source} · ` : ''}{t.name}{t.hop === 0 ? ' (start)' : ''}{t.saved === false ? ' (used, not saved)' : ''}</span>
            <span className='tdm-num'>{t.rowsExact ? '' : '≈ '}{fmtNum(t.rows)}</span>
            <span className='tdm-num'>{t.bytesExact ? '' : '≈ '}{fmtBytes(t.bytes)}</span>
            <span className='tdm-muted'>{t.method || ''}</span>
          </div>
        ))}
        {per.length > 40 && <div className='tdm-table__row'><span className='tdm-muted'>+ {per.length - 40} more tables</span><span /><span /><span /></div>}
        <div className='tdm-table__row'><span className='dg-name'>Total</span><span className='tdm-num dg-name'>{total.rowsExact ? '' : '≈ '}{fmtNum(total.rows)}</span><span className='tdm-num'>{fmtBytes(total.bytes)}</span><span /></div>
      </div>
      {budgetMaxBytes ? (
        <div className={`tdm-budget-line${over ? ' tdm-budget-line--over' : ''}`}>
          {over ? 'Over budget' : 'Within budget'} — {total.bytesExact ? '' : '≈ '}{fmtBytes(total.bytes)} of {fmtBytes(budgetMaxBytes)}{over ? ' · admission stops at whole-household boundaries' : ''}
        </div>
      ) : null}
      {Array.isArray(r.followers) && r.followers.length > 0 && (
        <div className='tdm-note'>{r.followers.map((f) => `${f.source}: ${fmtNum(f.keysMatched)} of ${fmtNum(f.keysRequested)} keys found`).join(' · ')}</div>
      )}
      {Array.isArray(r.warnings) && r.warnings.length > 0 && <div className='tdm-error'>{r.warnings.join(' · ')}</div>}
      {objectReport}
    </div>
  );
};

// Subset editor: which sources it pulls from (the parent selects, the rest follow by the shared
// key), what is selected from the parent, how far the traversal goes, the size budget, how the
// staged rows get masked, where runs are stored — plus preview counts and the run history.
const SubsetEditor = () => {
  const { confirm, confirmDialog } = useConfirm();
  const history = useHistory();
  const { id } = useParams();
  // `?pick=source` swaps the body for the connection picker; the component stays mounted, so the
  // form survives the round trip. Read at render — the loader below keys off [id, isNew] only.
  const pick = new URLSearchParams(useLocation().search).get('pick');
  const { connections, isReadOnly } = useContext(AppContext);
  const isNew = !id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [sources, setSources] = useState([]);
  // Every named data graph, by data source: what each source can be pulled through.
  const [graphs, setGraphs] = useState([]);
  const [stagings, setStagings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [engineJobs, setEngineJobs] = useState({ serviceId: null, items: null, error: null });
  const [subset, setSubset] = useState(null);
  const [building, setBuilding] = useState(false);
  // Cleared on unmount so a Manual build still waiting for a profile stops asking.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  // The last from-source-discovery build: which discovery each source used, columns marked, warnings.
  const [sourceDiscovery, setSourceDiscovery] = useState(null);
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    let alive = true;
    // A failed graph list leaves every source "not configured" rather than blocking the page.
    Promise.all([listStaging(), getProviders('MASKING'), isNew ? null : getSubset(id), isNew ? [] : listSubsetRuns(id),
      listDataGraphs().catch(() => [])])
      .then(([st, pv, s, rs, gs]) => {
        if (!alive) return;
        setStagings(Array.isArray(st) ? st : []);
        setProviders(Array.isArray(pv) ? pv : []);
        setGraphs(Array.isArray(gs) ? gs : []);
        if (s) {
          setSubset(s);
          setForm(fromResponse(s));
          setSources(sourcesFromResponse(s));
          setPreview(s.lastPreview ? { ...s.lastPreview, state: s.lastPreview.state, result: s.lastPreview.result } : null);
        } else if (Array.isArray(st) && st.length === 1) {
          setForm((f) => ({ ...f, stagingId: String(st[0].id) }));
        }
        setRuns(Array.isArray(rs) ? rs : []);
        setLoading(false);
      })
      .catch((e) => { if (alive) { setError(errorText(e, 'Could not load the subset')); setLoading(false); } });
    return () => { alive = false; };
  }, [id, isNew]);

  // ---- sources: the first one added is the parent; removing the parent promotes the next ----
  const eligible = useMemo(() => (connections || []).filter(isTdmConnection), [connections]);
  const graphsBySource = useMemo(() => {
    const by = {};
    graphs.forEach((g) => { (by[String(g.dataSourceId)] = by[String(g.dataSourceId)] || []).push(g); });
    return by;
  }, [graphs]);
  const addSource = (dsId) => {
    const c = (connections || []).find((x) => String(x.id) === String(dsId));
    if (!c) return;
    const row = sourceFromConnection(c, graphsBySource[String(c.id)]);
    setSources((ms) => (ms.some((m) => String(m.dataSourceId) === String(dsId)) ? ms : [...ms, { ...row, parent: ms.length === 0 }]));
  };
  const removeSource = (dsId) => setSources((ms) => {
    const next = ms.filter((m) => String(m.dataSourceId) !== String(dsId));
    if (next.length > 0 && !next.some((m) => m.parent)) next[0] = { ...next[0], parent: true };
    return next;
  });
  const setParent = (dsId) => setSources((ms) => ms.map((m) => ({ ...m, parent: String(m.dataSourceId) === String(dsId) })));
  const setGraph = (dsId, graphId) => setSources((ms) => ms.map((m) => (String(m.dataSourceId) === String(dsId) ? { ...m, dataGraphId: graphId } : m)));
  const openPicker = () => history.push({ search: '?pick=source' });
  const closePicker = () => history.replace({ search: '' });
  // The picker hands over every ticked connection at once; addSource's functional updates chain, so
  // the first of them becomes the parent when the subset has none yet.
  const pickSource = (ids) => { (Array.isArray(ids) ? ids : [ids]).forEach(addSource); closePicker(); };

  const parent = parentOf({ sources });
  // The parent's data graph: its key table is where row selection starts (not typed here), its key
  // name is the shared key's.
  const parentGraph = parent ? chosenGraph(parent, graphsBySource) : null;
  const parentKeyTable = parentGraph ? parentGraph.keyTable : null;
  const kind = kindForEngine(parent && parent.engine);
  // One component, kind-keyed wording: a Salesforce admin reads "objects" and "records", a DBA
  // reads "tables" and "rows"; the request shape underneath is identical.
  const copy = kind === 'SALESFORCE' ? {
    traversal: 'Lookup / master-detail traversal',
    selectionTitle: 'Record selection',
    selectionSub: 'Which records of the start object are taken; related objects follow through lookups, master-detail relationships and manual links from the Data Graph.',
    modes: [['ALL', 'All records'], ['WHERE', 'SOQL where'], ['SEEDS', 'Seed ids']],
    tableLabel: 'Start object', keyLabel: 'Key field',
    wherePlaceholder: "MailingState = 'MA' AND Type__c = 'Member'",
  } : kind === 'MONGO' ? {
    traversal: 'Shared-key traversal',
    selectionTitle: 'Document selection',
    selectionSub: 'Which documents of the starting collection are taken; other collections follow by the shared key and manual links from the Data Graph.',
    modes: [['ALL', 'All documents'], ['WHERE', 'Filter'], ['SEEDS', 'Seed ids']],
    tableLabel: 'Collection', keyLabel: 'Key field',
    wherePlaceholder: '{"state": "MA"}',
  } : {
    traversal: 'FK graph traversal',
    selectionTitle: 'Row selection',
    selectionSub: 'Which rows of the starting table are taken; linked tables follow through FKs and manual links from the Data Graph.',
    modes: [['ALL', 'All rows'], ['WHERE', 'Where'], ['SEEDS', 'Seed ids']],
    tableLabel: 'Table', keyLabel: 'Key column',
    wherePlaceholder: "state = 'MA' AND plan = 'HMO'",
  };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Seed ids are matched on the parent graph's key column. Parsed once per edit, not per render:
  // a pasted or loaded list can hold 100K ids.
  const seeds = useMemo(() => uniqueSeeds(parseSeeds(form.seedsText)), [form.seedsText]);
  const seedFileRef = useRef(null);
  const [seedFileNote, setSeedFileNote] = useState(null);
  const loadSeedFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                       // the same file can be chosen again
    if (!file) return;
    if (file.size > SEED_FILE_MAX_BYTES) {
      setSeedFileNote({ tone: 'danger', text: `${file.name} is larger than ${fmtBytes(SEED_FILE_MAX_BYTES)}.` });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const loaded = uniqueSeeds(parseSeeds(String(reader.result || '')));
      update('seedsText', loaded.ids.join('\n'));
      setSeedFileNote({ tone: 'muted', text: `Loaded ${fmtNum(loaded.ids.length)} ids from ${file.name}` });
    };
    reader.onerror = () => setSeedFileNote({ tone: 'danger', text: `Could not read ${file.name}.` });
    reader.readAsText(file);
  };
  // A Salesforce parent picks its budget by sandbox: a preset fills the size, Custom keeps it for editing.
  const budgetSandbox = form.budgetCustom ? 'CUSTOM' : (sandboxForBytes(sizeToBytes(form.budgetSize, form.budgetSizeUnit)) || 'CUSTOM');
  const pickSandbox = (key) => {
    const preset = SALESFORCE_SANDBOXES.find((s) => s.key === key);
    if (!preset) { update('budgetCustom', true); return; }
    const size = bytesToSize(preset.bytes);
    setForm((f) => ({ ...f, budgetSize: size.value, budgetSizeUnit: size.unit, budgetCustom: false }));
  };

  const delphixEngines = useMemo(() => enginesForMode(providers, 'DELPHIX_MANAGED'), [providers]);
  const modeEngines = useMemo(() => enginesForMode(providers, form.maskingMode), [providers, form.maskingMode]);
  // A Delphix mode needs its engine named; with exactly one Delphix registration there is nothing to choose.
  useEffect(() => {
    if (!isDelphixMode(form.maskingMode)) return;
    const current = providers.find((p) => p.serviceId === form.maskingRegisteredServiceId);
    if ((!current || !isDelphixEngine(current)) && delphixEngines.length >= 1) {
      setForm((f) => ({ ...f, maskingRegisteredServiceId: delphixEngines[0].serviceId }));
    }
  }, [form.maskingMode, form.maskingRegisteredServiceId, providers, delphixEngines]);
  // PLATFORM with one C2 masking service: there is nothing to choose, so the subset uses the
  // registry default (blank) rather than pinning that one registration by id. Waits for the
  // providers so a pinned choice among several is not cleared before they load.
  const platformEngineFixed = form.maskingMode === 'PLATFORM' && modeEngines.length <= 1;
  useEffect(() => {
    if (form.maskingMode !== 'PLATFORM' || !form.maskingRegisteredServiceId) return;
    const current = providers.find((p) => p.serviceId === form.maskingRegisteredServiceId);
    if ((current && isDelphixEngine(current)) || (providers.length > 0 && platformEngineFixed)) {
      setForm((f) => ({ ...f, maskingRegisteredServiceId: '' }));
    }
  }, [form.maskingMode, form.maskingRegisteredServiceId, providers, platformEngineFixed]);
  // The existing-job picker reads the engine's own jobs live (GET /api/v1/tdm/masking-engines/{id}/jobs).
  useEffect(() => {
    if (form.maskingMode !== 'DELPHIX_EXISTING_JOB' || !form.maskingRegisteredServiceId) return undefined;
    let alive = true;
    const serviceId = form.maskingRegisteredServiceId;
    setEngineJobs({ serviceId, items: null, error: null });
    listMaskingEngineJobs(serviceId)
      .then((items) => { if (alive) setEngineJobs({ serviceId, items: Array.isArray(items) ? items : [], error: null }); })
      .catch((e) => { if (alive) setEngineJobs({ serviceId, items: [], error: errorText(e, 'Could not list the engine jobs') }); });
    return () => { alive = false; };
  }, [form.maskingMode, form.maskingRegisteredServiceId]);

  // The subset is re-read with the runs: a DISCOVER step sets its inventory id while the page is open.
  const refreshRuns = useCallback(async () => {
    if (isNew) return;
    try {
      const [r, s] = await Promise.all([listSubsetRuns(id), getSubset(id)]);
      setRuns(r);
      if (s) setSubset(s);
    } catch (e) { /* keep the last list */ }
  }, [id, isNew]);
  const anyRunning = runs.some((r) => r.status === 'RUNNING' || r.status === 'PENDING');
  usePolling(refreshRuns, POLL_MS, anyRunning);

  const refreshPreview = useCallback(async () => {
    if (isNew) return;
    try { setPreview(await getPreview(id)); } catch (e) { /* ignore */ }
  }, [id, isNew]);
  usePolling(refreshPreview, 3000, Boolean(preview && preview.state === 'RUNNING'));

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const body = toBody(form, sources, parent && parent.engine);
      const saved = isNew ? await createSubset(body) : await updateSubset(id, body);
      setSubset(saved);
      if (Array.isArray(saved.sources) && saved.sources.length > 0) setSources(sourcesFromResponse(saved));
      setNotice({ tone: 'success', text: `Saved ${saved.name}.` });
      if (isNew) history.replace(`/tdm/subsets/${saved.id}`);
      return saved;
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Save failed') });
      return null;
    } finally {
      setSaving(false);
    }
  };

  // The Save button is the "done" action: it saves and returns to the landscape. Every other action
  // (preview, run, build inventory) saves through save() and stays on the editor.
  const saveAndClose = async () => {
    const saved = await save();
    if (saved) history.push('/tdm');
  };

  const doPreview = async () => {
    const saved = await save();
    if (!saved) return;
    try {
      setPreview(await startPreview(saved.id));
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Preview could not start') });
    }
  };

  const runNow = async () => {
    const saved = await save();
    if (!saved) return;
    try {
      await startSubsetRun(saved.id);
      setNotice({ tone: 'success', text: 'Run started — it lands in staging as a dated folder.' });
      await refreshRuns();
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Run could not start') });
    }
  };

  // Manual: build from the profile. A source with no profile is profiled by the platform first —
  // it answers { profilePending, profiling: [{ slug, jobId }] } instead of an inventory and builds on
  // a later call, so keep asking every profilePollMs() until it does (or the page goes away).
  const buildFromProfile = async () => {
    const saved = await save();
    if (!saved) return;
    setBuilding(true);
    try {
      let res = await buildInventoryFromProfile(saved.id);
      while (res && res.profilePending && alive.current) {
        const names = (res.profiling || []).map((p) => p.slug || `data source #${p.dataSourceId}`).join(', ');
        setNotice({ tone: 'info', text: `Fingerprinting ${names} first — the inventory is built as soon as the fingerprint finishes.` });
        await new Promise((resolve) => setTimeout(resolve, profilePollMs()));
        if (!alive.current) return;
        res = await buildInventoryFromProfile(saved.id);
      }
      if (!alive.current) return;
      const next = res && res.subset ? res.subset : res;
      setSubset(next);
      setForm((f) => ({ ...f, inventoryMode: next.inventoryMode || 'PROFILE' }));
      setNotice({ tone: 'success', text: `Inventory #${next.maskingInventoryId} built from the fingerprint — open it to choose what to mask.` });
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'The inventory could not be built') });
    } finally {
      if (alive.current) setBuilding(false);
    }
  };

  const buildFromSourceDiscovery = async () => {
    const saved = await save();
    if (!saved) return;
    setBuilding(true);
    try {
      const result = await buildInventoryFromSourceDiscovery(saved.id);
      setSourceDiscovery(result);
      const next = await getSubset(saved.id);
      setSubset(next);
      setForm((f) => ({ ...f, inventoryMode: next.inventoryMode || 'SOURCE_DISCOVERY' }));
      const warned = result && Array.isArray(result.warnings) && result.warnings.length > 0;
      setNotice({
        tone: warned ? 'warning' : 'success',
        text: `Inventory #${result.inventoryId} built from the last discover${warned ? ' — some columns need your decision (see below)' : ''}. Open it to review what is masked.`,
      });
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'The inventory could not be built from the last discover') });
    } finally {
      setBuilding(false);
    }
  };

  const remove = async () => {
    if (!(await confirm({ title: `Delete subset "${form.name}"?`, body: 'Staged runs stay in the bucket until each is purged.', confirmLabel: 'Delete subset', variant: 'danger' }))) return;
    // A 409 names the provision runs that still reference this subset — show the server's sentence.
    try { await deleteSubset(id); history.push('/tdm'); } catch (err) { setNotice({ tone: 'danger', text: errorText(err, 'Delete failed') }); }
  };

  const cancel = async (runId) => { try { await cancelRun(runId); await refreshRuns(); } catch (err) { setNotice({ tone: 'danger', text: errorText(err, 'Cancel failed') }); } };
  const purge = async (runId) => {
    if (!(await confirm({ title: 'Delete this run?', body: 'Its folder is purged from staging. This cannot be undone.', confirmLabel: 'Delete run', variant: 'danger' }))) return;
    try { await deleteRun(runId, true); await refreshRuns(); } catch (err) { setNotice({ tone: 'danger', text: errorText(err, 'Delete failed') }); }
  };

  const staging = stagings.find((s) => String(s.id) === String(form.stagingId));
  // The Delphix modes are offered only when a Delphix engine is registered — hidden, not disabled.
  // The one exception is a saved Delphix mode whose engine has since gone: its card stays so the
  // value is not silently lost, with a note saying why it cannot be saved as is.
  const delphixGone = isDelphixMode(form.maskingMode) && delphixEngines.length === 0;
  // PLATFORM and DELPHIX_MANAGED mask from the subset's inventory; existing-job and none do not.
  const usesInventory = form.maskingMode !== 'NONE' && form.maskingMode !== 'DELPHIX_EXISTING_JOB';
  const maskingModes = MASKING_MODES.filter(([v]) => !isDelphixMode(v) || delphixEngines.length > 0 || form.maskingMode === v);
  const maskingModeHelp = (MASKING_MODES.find(([v]) => v === form.maskingMode) || [])[2] || null;

  // Imported from a staging root's subset.json: read-only here (no sources to run from). Its runs
  // can still be provisioned or deleted.
  const imported = Boolean(subset && subset.origin === 'IMPORTED');
  const importedParent = imported ? ((subset.importedSources || []).find((m) => m.parent) || null) : null;

  const canSave = !isReadOnly && !imported && Boolean(form.name) && sources.length > 0 && Boolean(parent)
    && sources.every((m) => !sourceGraphProblem(m, graphsBySource, sources.length))
    && Boolean(form.stagingId) && Boolean(parentKeyTable) && (form.mode !== 'WHERE' || Boolean(form.where)) && (form.mode !== 'SEEDS' || seeds.ids.length > 0)
    && (!isDelphixMode(form.maskingMode) || Boolean(form.maskingRegisteredServiceId)) && !delphixGone
    && (form.maskingMode !== 'DELPHIX_EXISTING_JOB' || Boolean(form.externalMaskingJobId));

  if (pick === 'source') {
    return (
      <FetchContainer fetchingData={loading} absolute={true} error={error}>
        <SourcePickerView
          subsetName={isNew ? '' : form.name}
          connections={eligible}
          exclude={sources.map((m) => m.dataSourceId)}
          onAdd={pickSource}
          onCancel={closePicker}
        />
      </FetchContainer>
    );
  }

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='tdm-page tdm-page--narrow tdm-editor'>
        {confirmDialog}
        <Breadcrumbs items={[{ label: 'Test Data Landscape', to: '/tdm' }, { label: isNew ? 'New subset' : form.name }]} />
        <PageHeader
          icon={BiFilterAlt}
          eyebrow='Test Data Management / Subset'
          header={isNew ? 'New subset' : form.name || 'Subset'}
          tooltip='A reusable selection: its runs land in staging and any of them can be deployed into targets'
          meta={<span>{imported
            ? `Imported${importedParent ? ` · ${importedParent.engine ? `${String(importedParent.engine).toUpperCase()} · ` : ''}${importedParent.dataSourceName}` : ''}`
            : (parent ? `${parent.engine ? String(parent.engine).toUpperCase() : ''} · ${parent.dataSourceName} · ${copy.traversal}` : 'Add a source')}{subset && subset.slug ? ` · ${subset.slug}` : ''}</span>}
          actions={[
            ...(imported ? [] : [
              <Dropdown.Item key='preview' data-testid='tdm-action-preview' onClick={doPreview} disabled={saving || !canSave}>
                Preview counts
              </Dropdown.Item>,
              <Dropdown.Item key='run-now' data-testid='tdm-action-run-now' onClick={runNow} disabled={saving || !canSave || anyRunning}>
                Save &amp; run now
              </Dropdown.Item>,
            ]),
            ...(isNew ? [] : [
              <Dropdown.Item key='provision' data-testid='tdm-action-provision' onClick={() => history.push(`/tdm/subsets/${id}/provision`)}>
                Deploy
              </Dropdown.Item>,
              <Dropdown.Item key='delete' data-testid='tdm-action-delete' onClick={remove} disabled={anyRunning || !!isReadOnly}>
                Delete
              </Dropdown.Item>,
            ]),
          ]}
        />
        {notice && <Alert variant={notice.tone} className='py-1 px-2'>{notice.text}</Alert>}
        {imported && (
          <Alert variant='info' className='py-1 px-2' data-testid='tdm-subset-imported'>
            Imported from staging{staging ? ` '${staging.name}'` : ''} — read-only. This subset was read from <span className='tdm-mono'>{subset.slug}/subset.json</span>;
            it is not attached to data sources here and cannot be edited, previewed or exported. Its runs can be deployed when their masking finished cleanly, or deleted.
          </Alert>
        )}

        <div className='tdm-outline'>
        <fieldset disabled={imported} className='tdm-fieldset'>
        <div className='tdm-section'>
          <div className='tdm-section__title'>Subset</div>
          <Row className='text-left mb-2'>
            <Col xs={5}><Form.Group><Form.Label>Name <span className='text-danger'>*</span></Form.Label>
              <Form.Control size='sm' value={form.name} onChange={(e) => update('name', e.target.value)} data-testid='tdm-subset-name' /></Form.Group></Col>
            <Col xs={7}><Form.Group><Form.Label>Description</Form.Label>
              <Form.Control size='sm' value={form.description} onChange={(e) => update('description', e.target.value)} /></Form.Group></Col>
          </Row>
        </div>

        {imported ? <ImportedSourcesSection sources={subset.importedSources} sharedKeyName={subset.sharedKeyName} /> : (
          <SourcesSection
            sources={sources}
            graphsBySource={graphsBySource}
            onSetGraph={setGraph}
            onSetParent={setParent}
            onRemove={removeSource}
            onAdd={openPicker}
          />
        )}

        <div className='tdm-section'>
          <SectionTitle title={copy.selectionTitle} help={copy.selectionSub} />
          <Row className='text-left mb-2'>
            <Col xs={3}><Form.Group><Form.Label>Select</Form.Label>
              <Form.Control as='select' size='sm' value={form.mode} onChange={(e) => update('mode', e.target.value)} data-testid='tdm-subset-mode'>
                {copy.modes.map(([v, t]) => <option key={v} value={v} data-testid={`tdm-mode-${v}`}>{t}</option>)}
              </Form.Control></Form.Group></Col>
            {/* Not typed: the table and key are the parent data graph's, so the selection always starts
                where the graph's key lives (and followers are matched on it). A consultant moves them by
                editing the graph; an imported subset shows what its file recorded. */}
            <Col xs={4}>
              <Form.Group className='mb-1'><Form.Label>{copy.tableLabel}</Form.Label>
                <Form.Control plaintext readOnly size='sm' className='tdm-mono' data-testid='tdm-subset-table'
                  value={(imported ? subset.selection && subset.selection.startTable : parentKeyTable) || '—'} /></Form.Group>
              <Form.Group><Form.Label>{copy.keyLabel}</Form.Label>
                <Form.Control plaintext readOnly size='sm' className='tdm-mono' data-testid='tdm-subset-key'
                  value={(imported ? importedParent && importedParent.keyColumn : parentGraph && parentGraph.keyColumn) || '—'} /></Form.Group>
            </Col>
            {form.mode === 'WHERE' && (
              <Col xs={5}><Form.Group><Form.Label>Where <span className='text-danger'>*</span></Form.Label>
                <Form.Control size='sm' placeholder={copy.wherePlaceholder} value={form.where} onChange={(e) => update('where', e.target.value)} data-testid='tdm-subset-where' /></Form.Group></Col>
            )}
            {form.mode === 'SEEDS' && (
              <Col xs={5}><Form.Group><Form.Label>Seed ids <span className='text-danger'>*</span></Form.Label>
                <Form.Control as='textarea' rows={10} size='sm' className='tdm-seeds' placeholder='one per line or comma-separated, or load a file'
                  value={form.seedsText} onChange={(e) => update('seedsText', e.target.value)} data-testid='tdm-subset-seeds' />
                <div className='d-flex align-items-center'>
                  <Form.Text className='text-muted mr-auto' data-testid='tdm-subset-seeds-count'>
                    {fmtNum(seeds.ids.length)} ids{seeds.duplicates > 0 ? ` · ${fmtNum(seeds.duplicates)} duplicates dropped` : ''}
                  </Form.Text>
                  <input type='file' accept='.txt,.csv,.tsv,text/plain,text/csv' ref={seedFileRef} onChange={loadSeedFile}
                    style={{ display: 'none' }} data-testid='tdm-subset-seeds-file' />
                  <Button size='sm' variant='link' className='p-0' onClick={() => seedFileRef.current && seedFileRef.current.click()}
                    data-testid='tdm-subset-seeds-upload'>Load from file</Button>
                </div>
                {seedFileNote && (
                  <Form.Text className={seedFileNote.tone === 'danger' ? 'text-danger' : 'text-muted'} data-testid='tdm-subset-seeds-file-note'>
                    {seedFileNote.text}
                  </Form.Text>
                )}
              </Form.Group></Col>
            )}
          </Row>
          <Row className='text-left'>
            <Col xs={3}><Form.Group><Form.Label>Budget <HelpIcon text={budgetBasis(kind)} /></Form.Label>
              {/* One size budget for the whole subset, every table together; what a byte means follows the parent's kind. */}
              {kind === 'SALESFORCE' && (
                <Form.Control as='select' size='sm' className={budgetSandbox === 'CUSTOM' ? 'mb-1' : undefined} value={budgetSandbox}
                  onChange={(e) => pickSandbox(e.target.value)} data-testid='tdm-subset-budget-sandbox'>
                  {SALESFORCE_SANDBOXES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  <option value='CUSTOM'>Custom size…</option>
                </Form.Control>
              )}
              {(kind !== 'SALESFORCE' || budgetSandbox === 'CUSTOM') && (
                <InputGroup size='sm'>
                  <Form.Control type='number' min='0' step='any' placeholder='no cap' value={form.budgetSize} onChange={(e) => update('budgetSize', e.target.value)} data-testid='tdm-subset-budget' />
                  <InputGroup.Append>
                    <Form.Control as='select' value={form.budgetSizeUnit} onChange={(e) => update('budgetSizeUnit', e.target.value)} data-testid='tdm-subset-budget-unit'>
                      {Object.keys(SIZE_UNITS).map((u) => <option key={u} value={u}>{u}</option>)}
                    </Form.Control>
                  </InputGroup.Append>
                </InputGroup>
              )}
              <Form.Text className='text-muted' data-testid='tdm-subset-budget-basis'>{kind === 'SALESFORCE' ? 'Salesforce storage accounting' : 'Size at the source'}</Form.Text>
              {form.legacyBudgetMaxRows != null && (
                <Form.Text className='text-warning' data-testid='tdm-subset-budget-legacy'>
                  Saved with a row budget of {fmtNum(form.legacyBudgetMaxRows)} {unitNoun(kind)}; budgets are sizes now, and saving replaces it with the size above.
                </Form.Text>
              )}
            </Form.Group></Col>
          </Row>
        </div>

        <div className='tdm-section'>
          <SectionTitle title='Masking' help="Masking uses the subset's inventory, which you can edit: platform masking masks every row before it is staged, Delphix masks the staged files in place afterwards. A run whose masking fails cannot be deployed; its staged files stay until you delete the run." />
          {delphixGone && <div className='tdm-error mb-2' data-testid='tdm-delphix-gone'>Its Delphix engine is no longer registered — pick another masking mode.</div>}
          <Row className='text-left'>
            <Col xs={4}><Form.Group>
              <Form.Label className='d-flex align-items-center'>Masking
                {maskingModeHelp && <span className='ml-1'><HelpIcon help={maskingModeHelp} testId='tdm-masking-help' /></span>}
              </Form.Label>
              <Form.Control as='select' size='sm' value={form.maskingMode} onChange={(e) => update('maskingMode', e.target.value)} data-testid='tdm-subset-masking'>
                {maskingModes.map(([v, t]) => <option key={v} value={v} data-testid={`tdm-masking-${v}`}>{t}</option>)}
              </Form.Control>
            </Form.Group></Col>
            {form.maskingMode !== 'NONE' && (
              <Col xs={4}><Form.Group><Form.Label>Masking engine{isDelphixMode(form.maskingMode) ? <span className='text-danger'> *</span> : null}</Form.Label>
                {platformEngineFixed ? (
                  <Form.Control plaintext readOnly size='sm' value={engineLabel(modeEngines[0]) || 'C2 masking service'} data-testid='tdm-subset-engine-fixed' />
                ) : (
                  <Form.Control as='select' size='sm' value={form.maskingRegisteredServiceId} onChange={(e) => update('maskingRegisteredServiceId', e.target.value)} data-testid='tdm-subset-engine'>
                    {form.maskingMode === 'PLATFORM' && <option value=''>Default masking service</option>}
                    {isDelphixMode(form.maskingMode) && modeEngines.length !== 1 && <option value=''>Choose a Delphix engine…</option>}
                    {modeEngines.map((p) => <option key={p.serviceId} value={p.serviceId}>{engineLabel(p)}</option>)}
                  </Form.Control>
                )}
                {form.maskingMode === 'PLATFORM' && delphixEngines.length > 0 && (
                  <Form.Text className='text-muted'>Delphix engines are offered under the Delphix masking modes.</Form.Text>
                )}
              </Form.Group></Col>
            )}
            {form.maskingMode === 'DELPHIX_EXISTING_JOB' && (
              <Col xs={4}><Form.Group><Form.Label>Delphix job <span className='text-danger'>*</span></Form.Label>
                <Form.Control as='select' size='sm' value={form.externalMaskingJobId} onChange={(e) => update('externalMaskingJobId', e.target.value)} data-testid='tdm-subset-engine-job' disabled={!form.maskingRegisteredServiceId}>
                  <option value=''>{engineJobs.items === null && form.maskingRegisteredServiceId ? 'Loading jobs from the engine…' : 'Choose a job…'}</option>
                  {(engineJobs.items || []).map((j) => (
                    <option key={j.externalJobId} value={j.externalJobId} disabled={!j.usable}>
                      {j.name} (#{j.externalJobId}){j.usable ? '' : ` — ${j.reason || 'not usable'}`}
                    </option>
                  ))}
                </Form.Control>
                {engineJobs.error && <Form.Text className='text-danger' data-testid='tdm-subset-engine-job-error'>{engineJobs.error}</Form.Text>}
                {!engineJobs.error && engineJobs.items && engineJobs.items.length === 0 && <Form.Text className='text-muted'>The engine has no masking jobs.</Form.Text>}
                {!engineJobs.error && engineJobs.items && engineJobs.items.length > 0 && (
                  <Form.Text className='text-muted'>Only in-place, multi-tenant FILE jobs can run against a staged run: the platform supplies this run's data/ connector per execution.</Form.Text>
                )}
              </Form.Group></Col>
            )}
            <Col xs={4}><Form.Group><Form.Label>Store runs in <span className='text-danger'>*</span></Form.Label>
              <Form.Control as='select' size='sm' value={form.stagingId} onChange={(e) => update('stagingId', e.target.value)} data-testid='tdm-subset-staging'>
                <option value=''>Choose staging…</option>
                {stagings.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.rootPath}</option>)}
              </Form.Control>
              {stagings.length === 0 && <Form.Text className='text-muted'><button type='button' className='tdm-link' onClick={() => history.push('/tdm/staging')}>Add a staging area first</button></Form.Text>}
              {form.maskingMode !== 'NONE' && staging && !isMaskableStaging(staging, form.maskingMode) && (
                <Form.Text className='text-danger' data-testid='tdm-subset-staging-unmaskable'>{isDelphixMode(form.maskingMode) ? 'A Delphix engine' : 'Platform masking'} cannot read {staging.type} staging — stage in {maskableStagingTypes(form.maskingMode).filter((t) => t !== 'LOCAL').join(', ')}, or set masking to None.</Form.Text>
              )}
            </Form.Group></Col>
          </Row>
          {/* Delphix reads and rewrites the staged files in place, so its runs cannot use the platform's C2E1 staging encryption. */}
          {isDelphixMode(form.maskingMode) && (
            <Alert variant='warning' className='py-1 px-2 mb-2' data-testid='tdm-delphix-unencrypted'>
              Delphix does not support encrypted subsets. Runs masked by Delphix are staged as plain CSV files, and the unmasked rows stay readable in staging until the Delphix job has masked them.
            </Alert>
          )}
          {usesInventory && (
            <>
              <Form.Label className='mt-2 d-block'>Masking inventory</Form.Label>
              <div className='mb-2'>
                {INVENTORY_MODES.map(([v, t, d]) => (
                  <Form.Check key={v} inline type='radio' name='tdm-inventory-mode' id={`tdm-inventory-mode-${v}`}
                    checked={form.inventoryMode === v} onChange={() => update('inventoryMode', v)} data-testid={`tdm-inventory-${v}`}
                    label={<DiscoveryTooltip placement='top' text={d}><span>{t}</span></DiscoveryTooltip>} />
                ))}
              </div>
              {form.inventoryMode === 'PROFILE' && (
                <div className='mb-2'>
                  <Button size='sm' variant='outline-primary' data-testid='tdm-inventory-build' onClick={buildFromProfile}
                    disabled={saving || building || !canSave || anyRunning}
                    title='Every column of every table the graph plan reaches, from the latest Fingerprint; a source with no fingerprint is fingerprinted first. Columns you already decided on keep your choice.'>
                    {building ? 'Building…' : (subset && subset.maskingInventoryId ? 'Refresh inventory' : 'Build inventory')}
                  </Button>
                  {subset && subset.maskingInventoryId && <span className='tdm-muted ml-2'>Adds tables and columns the fingerprint has gained; your choices are kept.</span>}
                </div>
              )}
              {form.inventoryMode === 'SOURCE_DISCOVERY' && (
                <div className='mb-2'>
                  <Button size='sm' variant='outline-primary' data-testid='tdm-inventory-build-discovery' onClick={buildFromSourceDiscovery}
                    disabled={saving || building || !canSave || anyRunning}
                    title='Every column of every table the graph plan reaches, pre-marked from the last completed discovery of each source. Columns you already decided on keep your choice.'>
                    {building ? 'Building…' : (subset && subset.maskingInventoryId ? 'Refresh from last discover' : 'Build inventory from last discover')}
                  </Button>
                  {subset && subset.maskingInventoryId && <span className='tdm-muted ml-2'>Re-reads each source\'s last discovery; your choices are kept.</span>}
                  {sourceDiscovery && <SourceDiscoverySummary result={sourceDiscovery} />}
                </div>
              )}
            </>
          )}
          {form.maskingMode === 'DELPHIX_EXISTING_JOB' && (
            <div className='tdm-note'>Every staged table must be covered by the engine job's ruleset: a staged file the engine does not report comes back FAILED, and the run cannot be deployed until masking succeeds.</div>
          )}
          {/* The inventory lives on the subset's raw view (rooted at the run's data/); the Inventory Manager's source page opens it
              directly when handed its id in location.state. Every Discover-mode run refreshes it. */}
          {usesInventory && subset && subset.maskingInventoryId && subset.rawViewDataSourceId && (
            <div className='tdm-note' data-testid='tdm-subset-inventory'>
              <Link data-testid='tdm-subset-inventory-link'
                to={{ pathname: `/inventory-manager/${subset.rawViewDataSourceId}`, state: { inventoryId: subset.maskingInventoryId } }}>
                Edit masking inventory #{subset.maskingInventoryId}
              </Link>
              {' '}on the subset's raw view (<Link to={`/sources/source-editor/${subset.rawViewDataSourceId}`}>data source #{subset.rawViewDataSourceId}</Link>).{' '}
              {subset.inventoryMode === 'PROFILE'
                ? 'Built from the fingerprint (Manual); every run masks with it as you edit it.'
                : subset.inventoryMode === 'SOURCE_DISCOVERY'
                  ? 'Built from the sources\' last discover; every run masks with it as you edit it — refresh it after re-discovering a source.'
                  : 'Built from the sources\' discovery; every run re-discovers the sources and refreshes it from the new results, keeping the columns you decided on in the editor.'}
            </div>
          )}
          {usesInventory && !(subset && subset.maskingInventoryId) && (
            <div className='tdm-note' data-testid='tdm-subset-inventory-none'>
              {form.inventoryMode === 'PROFILE'
                ? 'No masking inventory yet — build it from the fingerprint, then choose what to mask. A run cannot start without one.'
                : form.inventoryMode === 'SOURCE_DISCOVERY'
                  ? 'No masking inventory yet — build it from the last discover, then review what is masked. A run cannot start without one.'
                  : 'No masking inventory yet — the first run discovers the sources and builds one before staging, which you can then edit here.'}
            </div>
          )}
        </div>

        </fieldset>

        {!imported && (
          <div className='tdm-section'>
            <SectionTitle title='Preview' help='Dry traversal on the worker. Exact counts where a COUNT is cheap; ≈ marks sampled or plan-ratio estimates.' />
            <PreviewPanel preview={preview} budgetMaxBytes={sizeToBytes(form.budgetSize, form.budgetSizeUnit)}
              onPreview={imported ? null : doPreview} canPreview={!saving && canSave} sources={sources} />
          </div>
        )}

        {!isNew && (
          <div className='tdm-section'>
            <SectionTitle title={`Runs · ${runs.length}`} testId='tdm-runs-help'
              help={staging ? `${staging.rootPath}/${subset ? subset.slug : ''}/ — pick a run on the Deploy screen to load it into targets.` : 'Pick a run on the Deploy screen to load it into targets.'} />
            {runs.length === 0 && <div className='tdm-empty'>No runs yet.</div>}
            {runs.length > 0 && (
              <div className='tdm-table tdm-table--runs'>
                <div className='tdm-table__head'><span>Started</span><span>Status</span><span>Steps</span><span>Rows</span><span /></div>
                {runs.map((r) => (
                  <div className='tdm-table__row' key={r.id} data-testid={`tdm-run-${r.id}`}>
                    <span>
                      <Link to={`/tdm/runs/${r.id}`} data-testid={`tdm-run-${r.id}-open`} title={`Open run #${r.id}`}>{fmtWhen(r.startedAt)}</Link>
                      <div className='tdm-mono tdm-muted'>…/{r.runPath}/</div>
                    </span>
                    <span>
                      <span className={`tdm-pill tdm-pill--${RUN_STATUS_TONE[r.status] || 'neutral'}`}>{r.status}</span>
                      {r.budgetExceeded ? <div className='tdm-note'>budget hit</div> : null}
                      {r.provisionBlocker ? <div className='tdm-note tdm-error' data-testid={`tdm-run-${r.id}-blocker`}>{r.provisionBlocker}</div> : null}
                    </span>
                    <span>
                  <RunStepStrip steps={r.steps} compact />
                  {r.error ? <div className='tdm-error'>{r.error}</div> : null}
                  <DiscoverWarnings steps={r.steps} />
                </span>
                    <span>{r.totals ? `${fmtNum(r.totals.rows)} rows · ${fmtBytes(r.totals.bytes)}` : '—'}</span>
                    <span className='dg-actions'>
                      <Link to={`/tdm/runs/${r.id}`} className='tdm-link' data-testid={`tdm-run-${r.id}-details`}>Details</Link>
                      {!isReadOnly && (r.status === 'RUNNING' || r.status === 'PENDING') && <button type='button' className='tdm-link' onClick={() => cancel(r.id)}>{r.cancelRequested ? 'Force cancel' : 'Cancel'}</button>}
                      {!isReadOnly && r.status !== 'RUNNING' && r.status !== 'PENDING' && <button type='button' className='tdm-link' onClick={() => purge(r.id)}>Delete</button>}
                    </span>
                    <RunObjectSummary run={r} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
        <div className='tdm-editor__footer'>
          <span className='tdm-note'>{MASKING_MODE_LABEL[form.maskingMode]} · {staging ? staging.name : 'no staging'}</span>
          {!imported && <Button size='sm' onClick={saveAndClose} disabled={saving || !canSave} data-testid='tdm-subset-save'>Save subset</Button>}
        </div>
      </div>
    </FetchContainer>
  );
};

export default SubsetEditor;
